import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import PageHeader from '../../components/misc/PageHeader';
import EmptyScreenView from '../../components/misc/EmptyScreenView';
import PrimaryButton from '../../components/buttons/PrimaryButton';
import MarketplaceProductsPage from '../financial/MarketplaceProductsPage';
import ContributionPlansPage from './ContributionPlansPage';
import MerchantAccountPage from './MerchantAccountPage';
import EventTicketsPage from './EventTicketsPage';
import { checkOnboarding, createMerchantAccount } from '../../../services/community/merchant.marketplace.service';
import { CheckOnboardingDto } from '../../../models/marketplace.model';

export default function SalesCollectPage(): JSX.Element {
  const { t } = useTranslation();
  const location = useLocation();

  const [onboardStatus, setOnboardStatus] = useState<CheckOnboardingDto | null>(null);
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [openOnboardingWhenReady, setOpenOnboardingWhenReady] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const status = await checkOnboarding();
        setOnboardStatus(status);
      } catch (error) {
        console.error('Error loading onboarding status:', error);
        setOnboardStatus(null);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (openOnboardingWhenReady && onboardStatus?.onboardingLink) {
      window.open(onboardStatus.onboardingLink, '_blank', 'noopener,noreferrer');
      setOpenOnboardingWhenReady(false);
    }
  }, [onboardStatus]);

  const handleCreateMerchantAccount = async () => {
    try {
      setCreatingAccount(true);
      const status = await createMerchantAccount();
      setOnboardStatus(status);
      toast.success(t('page.financial.salesCollect.toast.createSuccess'));
    } catch (error) {
      console.error('Error creating merchant account:', error);
      toast.error(t('page.financial.salesCollect.toast.createFailed'));
    } finally {
      setCreatingAccount(false);
    }
  };

  const onOnboardingWindow = () => {
    if (onboardStatus?.onboardingLink) {
      toast.info(t('page.financial.salesCollect.onboarding.toasts.opening'));
      window.open(onboardStatus.onboardingLink, '_blank', 'noopener,noreferrer');
    } else {
      toast.info(t('page.financial.salesCollect.onboarding.toasts.settingUp'));
      handleCreateMerchantAccount();
      setOpenOnboardingWhenReady(true);
    }
  };

  const getInitialTab = (): 'overview' | 'contribution' | 'products' | 'tickets' => {
    if (location.state?.activeTab) {
      return location.state.activeTab === 'paymentSettings'
        ? 'overview'
        : location.state.activeTab;
    }

    if (location.pathname.includes('/financial/contribution-plans')) {
      return 'contribution';
    }
    if (location.pathname.includes('/financial/merchantAccount')) {
      return 'overview';
    }
    if (location.pathname.includes('/financial/tickets')) {
      return 'tickets';
    }
    if (
      location.pathname.includes('/financial/products') ||
      location.pathname.includes('/financial/orders')
    ) {
      return 'products';
    }
    return 'overview';
  };

  const [activeTab, setActiveTab] = React.useState<'overview' | 'products' | 'contribution' | 'tickets'>(
    getInitialTab(),
  );
  
  React.useEffect(() => {
    if (location.state?.activeTab && location.state.activeTab !== activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);

  return (
    <>
      <div>
        <PageHeader
          title={t('page.financial.salesCollect.title')}
          subtitle={t('page.financial.salesCollect.subtitle')}
        />

        {(!onboardStatus || !onboardStatus.onboarded) ? (
          <EmptyScreenView
            title={t('page.financial.salesCollect.onboarding.requiredTitle')}
            subtitle={t('page.financial.salesCollect.onboarding.requiredSubtitle')}
            primaryButton={
              <PrimaryButton
                text={
                  creatingAccount
                    ? t('page.financial.salesCollect.onboarding.button.starting')
                    : !onboardStatus
                    ? t('page.financial.salesCollect.onboarding.button.start')
                    : t('page.financial.salesCollect.onboarding.button.finalize')
                }
                icon={FaExternalLinkAlt}
                onClick={() => onOnboardingWindow()}
                className="min-w-[200px]"
                loading={creatingAccount}
              />
            }
          />
        ) : (
          <>
            {/* Tabs */}
            <div className="mb-6 flex gap-4 border-b border-primary-300">
              {(['overview' /*, 'products' */, 'tickets' /*, 'contribution' */] as const).map((tabKey) => (
                <button
                  key={tabKey}
                  type="button"
                  onClick={() => setActiveTab(tabKey)}
                  className={`relative px-4 pb-1 pt-2 font-medium transition-all duration-150 ${activeTab === tabKey
                    ? 'text-primary-700 -mb-[1px] rounded-t-md border border-primary-300 border-b-secondary-50'
                    : 'text-primary-600 hover:text-primary-800'
                    }`}>
                  {t(`page.financial.salesCollect.tabs.${tabKey}`)}
                </button>
              ))}
            </div>

            <div className="w-full">
              {activeTab === 'overview' && <MerchantAccountPage />}
              {activeTab === 'tickets' && <EventTicketsPage />}
              {activeTab === 'products' && <MarketplaceProductsPage />}
              {activeTab === 'contribution' && <ContributionPlansPage />}
            </div>
          </>
        )}
      </div>
    </>
  );
}
